import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";
import {
  FieldDefSchema,
  type FieldDef,
  type FieldDefCreate,
  type FieldDefUpdate,
} from "#shared/schemas/fieldDef.ts";
import { api, ApiRequestError } from "@web/lib/api.ts";

const FieldsResponse = z.object({ fields: z.array(FieldDefSchema) });
const FieldCreated = z.object({ field: FieldDefSchema });
const FieldUpdated = z.object({ field: FieldDefSchema });
const FieldDeleted = z.object({ deleted_values: z.number() });

function failToast(err: unknown, fallback: string): void {
  toast.error(err instanceof ApiRequestError ? err.message : fallback);
}

/** Settings needs archived fields too — the sheet only lists live ones. */
export function useAllFieldDefs() {
  return useQuery({
    queryKey: ["fields", "all"],
    queryFn: async (): Promise<FieldDef[]> => {
      const body = FieldsResponse.parse(await api("/api/fields?include_archived=true"));
      return body.fields;
    },
  });
}

function invalidateFields(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ["fields"] });
}

export function useCreateField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: FieldDefCreate): Promise<FieldDef> => {
      const body = FieldCreated.parse(await api("/api/fields", { method: "POST", json: input }));
      return body.field;
    },
    onSuccess: () => {
      toast.success("Field created");
      invalidateFields(qc);
    },
    onError: (err) => failToast(err, "Failed to create field"),
  });
}

export function useUpdateField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; patch: FieldDefUpdate }): Promise<FieldDef> => {
      const body = FieldUpdated.parse(
        await api(`/api/fields/${input.id}`, { method: "PATCH", json: input.patch }),
      );
      return body.field;
    },
    onSuccess: () => {
      toast.success("Field saved");
      invalidateFields(qc);
    },
    onError: (err) => failToast(err, "Failed to save field"),
  });
}

export function useArchiveField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; archived: boolean }) =>
      api(`/api/fields/${input.id}`, {
        method: "PATCH",
        json: { archived: input.archived },
      }),
    onSuccess: (_data, input) => {
      toast.success(input.archived ? "Field archived" : "Field restored");
      invalidateFields(qc);
    },
    onError: (err) => failToast(err, "Failed to update field"),
  });
}

export function useReorderField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; after_id?: string | null; before_id?: string | null }) =>
      api(`/api/fields/${input.id}`, {
        method: "PATCH",
        json: {
          ...(input.after_id !== undefined ? { after_id: input.after_id } : {}),
          ...(input.before_id !== undefined ? { before_id: input.before_id } : {}),
        },
      }),
    onSuccess: () => {
      invalidateFields(qc);
    },
    onError: (err) => failToast(err, "Failed to reorder field"),
  });
}

/**
 * Destructive. The API refuses without `?confirm=true` and tells you how many
 * stored values would die — we always pass confirm after the in-app dialog.
 */
export function useDeleteField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<number> => {
      const body = FieldDeleted.parse(
        await api(`/api/fields/${id}?confirm=true`, { method: "DELETE" }),
      );
      return body.deleted_values;
    },
    onSuccess: (n) => {
      toast.success(n > 0 ? `Field deleted (${n} values removed)` : "Field deleted");
      invalidateFields(qc);
    },
    onError: (err) => failToast(err, "Failed to delete field"),
  });
}
